import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, setIn } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import isBoolean from 'common/util/isBoolean';
import firstWordCapitalized from 'common/util/firstWordCapitalized';

import {
  getHouseholdPets,
} from 'common/householdHelpers';

import {
  transformBooleanToYesNo,
} from 'client/helpers/formikHelpers';

import {
  PET_OPTION_VALUE_DOG,
  PET_OPTION_VALUE_CAT,
  PET_OPTION_VALUE_OTHER,
} from 'common/petOptionItems';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  getLatestStatus,
} from 'common/applicationAssemblyLineConstants';

import PetBreed from 'client/actions/PetBreed';
import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_TRANSPARENT_BLUE_BORDER,
  SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE,
} from 'client/components/SrButton/SrButton';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE,
} from 'client/components/SrFormikField';
import SrLabel from 'client/components/SrLabel/SrLabel';
import {
  SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED,
} from 'client/components/SrCollapsibleContainer/SrCollapsibleContainer';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrModalSplitView from 'client/components/SrModalSplitView/SrModalSplitView';
import SrApplicationAssemblyLineContainer from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainer';
import SrApplicationAssemblyLineContainerBoxComparision from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineContainerBoxComparisons';
import SrImageNotFound from 'client/components/SrImageNotFound';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import postWithState from 'client/actions/postWithState';
import { SR_API_ACTION_HOUSEHOLD_APPLICATION_MANUALLY_VERIFY_PETS } from 'api/actionTypes';

const withoutDots = (string) => string.replace(/\./g, '');

const getFormFields = ({
  pet,
  onChangeFormField,
  assemblyLine,
}) => {
  const sanitizedPetName = withoutDots(pet.name);
  return lodashCompact([
    pet.isEsa && {
      id: `pets.${sanitizedPetName}.letterheadIncluded`,
      title: 'Does the ESA/Service Animal document have a doctor’s letterhead?',
      type: 'yes_no',
      validations: ['required'],
    },
    pet.isEsa && {
      id: `pets.${sanitizedPetName}.letterheadIncludesName`,
      title: 'Does the doctor’s letter have any of the renter’s names on it?',
      type: 'yes_no',
      validations: ['required'],
      label: (
        <>
          <span className='title' style={{ marginTop: 20 }}>Renters:</span>
          <ul>
            {assemblyLine.renters.map((renter) => {
              return (
                <li>
                  <span className='title'>
                    {renter.name}
                  </span>
                </li>
              );
            }
            )}
          </ul>
        </>
      ),
    },
    pet.type === PET_OPTION_VALUE_DOG && {
      id: `pets.${sanitizedPetName}.matchBreed`,
      title: 'Does the breed in the applicant’s photo match the breed example?',
      type: 'yes_no',
      validations: ['required'],
    },
    pet.type === PET_OPTION_VALUE_DOG && {
      id: `pets.${sanitizedPetName}.matchColor`,
      title: `Is the color of the dog "${pet.color}"?`,
      type: 'yes_no',
      validations: ['required'],
    },
    pet.type === PET_OPTION_VALUE_DOG && {
      id: `pets.${sanitizedPetName}.matchWeight`,
      title: `Is the weight of the dog approximately "${pet.weight}lbs"?`,
      type: 'yes_no',
      validations: ['required'],
    },
    pet.type === PET_OPTION_VALUE_CAT && {
      id: `pets.${sanitizedPetName}.matchType`,
      title: 'Is this a cat?',
      type: 'yes_no',
      validations: ['required'],
    },
    pet.type === PET_OPTION_VALUE_OTHER && {
      id: `pets.${sanitizedPetName}.isCatOrDog`,
      title: 'Is the pet a cat or a dog?',
      type: 'yes_no',
      validations: ['required'],
    },
  ]).map((formField) => {
    return {
      ...formField,
      properties: {
        onChange: (value) => onChangeFormField(formField.id, value),
      },
    };
  });
};

const getPetStatus = ({
  pet,
  petValues,
}) => {
  let status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;
  if (Object.keys(petValues).length === 0) {
    return status;
  }

  switch (pet.type) {
    case PET_OPTION_VALUE_DOG: {
      const petFields = lodashCompact([
        'matchBreed',
        'matchColor',
        'matchWeight',
        pet.isEsa && 'letterheadIncluded',
        pet.isEsa && 'letterheadIncludesName',
      ]);

      if (petFields.every((petFieldId) => petValues[petFieldId])) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
      }
      else if (petFields.some((petFieldId) => isBoolean(petValues[petFieldId]))) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }
      break;
    }
    case PET_OPTION_VALUE_CAT: {
      const petFields = lodashCompact([
        'matchType',
        pet.isEsa && 'letterheadIncluded',
        pet.isEsa && 'letterheadIncludesName',
      ]);

      if (petFields.every((petFieldId) => petValues[petFieldId])) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
      }
      else if (petFields.some((petFieldId) => isBoolean(petValues[petFieldId]))) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }
      break;
    }
    case PET_OPTION_VALUE_OTHER: {
      const petFields = lodashCompact([
        'isCatOrDog',
        pet.isEsa && 'letterheadIncluded',
        pet.isEsa && 'letterheadIncludesName',
      ]);

      if (petValues.isCatOrDog === false &&
        (!pet.isEsa || (petValues.letterheadIncluded && petValues.letterheadIncludesName))) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED;
      }
      else if (petFields.some((petFieldId) => isBoolean(petValues[petFieldId]))) {
        status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS;
      }
      break;
    }
  }

  return status;
};

const getPets = ({
  assemblyLine,
  householdApplication,
}) => {
  const { pets } = getHouseholdPets({
    householdApplications: assemblyLine.householdApplications,
  });

  const petsWithStatus = pets.map((pet) => {
    return {
      ...pet,
      status: getPetStatus({
        pet,
        petValues: householdApplication.assemblyLineApplication?.pets[withoutDots(pet.name)] || {},
      }),
    };
  });

  return {
    pets: petsWithStatus,
    nonEsaPets: petsWithStatus.filter(({ isEsa }) => !isEsa),
    esaPets: petsWithStatus.filter(({ isEsa }) => isEsa),
  };
};

const SrApplicationAssemblyLineStepPetModal = ({
  pet,
  petIndex,
  householdApplication,
  onClose,
  updateAssemblyLineApplication,
  assemblyLine,
}) => {
  const formikRef = useRef(null);
  const [showAll, setShowAll] = useState(false);
  const { petBreed } = useBranch({ petBreed: ['petBreeds', 'byName', pet.breed || ''] });
  const dispatch = useDispatch();

  useEffect(() => {
    if (pet.breed) {
      dispatch(PetBreed.getByName, { name: pet.breed });
    }
  }, [pet, dispatch]);

  const petDetails = lodashCompact([
    {
      title: 'Type',
      value: pet.type,
    },
    {
      title: 'Age',
      value: pet.age,
    },
    {
      title: 'Gender',
      value: pet.gender,
    },
    {
      title: 'Declawed',
      value: transformBooleanToYesNo(pet.declawed),
    },
    {
      title: 'Caught up on rabies shots',
      value: transformBooleanToYesNo(pet.rabiesShotsCurrent),
    },
    pet.type === PET_OPTION_VALUE_DOG && {
      title: 'ESA/Service Animal',
      value: transformBooleanToYesNo(pet.isEsa),
    },
  ]);

  const leftChild = (
    <SrFlex direction='col' justify='center' align='center'>
      {pet.esaCertificate && (
        <>
          <SrText font='greycliff' color='white' weight='800' size='18'>
            ESA/Service Animal Documentation
          </SrText>

          {pet.esaCertificate && (
            <SrBox
              marginTop='20px'
            >
              <SrS3FileDisplay
                url={pet.esaCertificate}
                maxWidth='100%'
                maxHeight='800px'
                withRotateButton
              />
            </SrBox>
          )}
          <SrBox height='32px' />
        </>
      )}
      {pet.type === PET_OPTION_VALUE_DOG && (
        <SrFlex margin='0 0 56px'>
          <SrFlex direction='col' justify='center' align='center'>
            <SrText font='greycliff' color='white' weight='800' size='18'>
              Applicant’s Photo
            </SrText>
            {pet.photo && (
              <SrBox
                marginTop='20px'
              >
                <SrS3FileDisplay
                  url={pet.photo}
                  maxWidth='405px'
                  maxHeight='405px'
                  withRotateButton
                />
              </SrBox>
            )}
          </SrFlex>
          <SrBox width='20px' />
          <SrBox>
            <SrText font='greycliff' color='white' weight='800' size='18'>
              Breed Example: {pet.breed}
            </SrText>
            <SrBox
              marginTop='20px'
              width='405px'
              height='405px'
            >
              {petBreed?.photoUrl
                ? (
                  <SrS3FileDisplay
                    url={petBreed?.photoUrl}
                    maxWidth='405px'
                    maxHeight='405px'
                    withRotateButton
                  />
                )
                : <SrImageNotFound />
              }
            </SrBox>
          </SrBox>
        </SrFlex>
      )}
      <SrText font='greycliff' color='white' weight='800' size='18'>
        Applicant’s Photo
      </SrText>
      {pet.photo && (
        <SrBox
          marginTop='20px'
        >
          <SrS3FileDisplay
            url={pet.photo}
            maxWidth='100%'
            maxHeight='800px'
            withRotateButton
          />
        </SrBox>
      )}
    </SrFlex>
  );

  const rightChild = (
    <Formik
      ref={formikRef}
      initialValues={{
        pets: householdApplication.assemblyLineApplication?.pets || {},
      }}
      render={(formikBag) => {
        const formFields = getFormFields({
          pet,
          onChangeFormField: (fieldId, newValue) => {
            const updates = setIn(
              { ...formikBag.values },
              fieldId,
              newValue,
            );

            updateAssemblyLineApplication({
              assemblyLineApplicationId: householdApplication.assemblyLineApplication?.id,
              updates,
            });
          },
          assemblyLine,
        });
        return (
          <SrBox padding='24px'>
            <SrApplicationAssemblyLineContainer
              title={`Pet ${petIndex + 1}`}
              subtitle={pet.name}
              status={getPetStatus({
                pet,
                petValues: formikBag.values.pets[withoutDots(pet.name)] || {},
              })}
              borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
              isExpanded
            >
              <Form className='form'>
                {formFields.map((formField) => (
                  <SrFormikField
                    key={formField.id}
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE_ASSEMBLY_LINE}
                  />
                ))}
              </Form>
              {showAll && (
                <SrBox marginTop='32px'>
                  {petDetails.map(({ title, value }) => (
                    <SrBox key={title} marginBottom='16px'>
                      <SrText font='greycliff' size='14' color='grey600' weight='700'>{title}</SrText>
                      <br />
                      <SrText font='greycliff' weight='700'>
                        {firstWordCapitalized(value)}
                      </SrText>
                    </SrBox>
                  ))}
                </SrBox>
              )}
              {pet.type !== PET_OPTION_VALUE_OTHER && (
                <SrFlex marginTop='32px' justify='center'>
                  <SrLabel
                    label={showAll ? 'Hide details' : 'View more details'}
                    borderColor='#EAEBEC'
                    fontSize='normal'
                    padding='4px 24px'
                    onClick={() => setShowAll(!showAll)}
                  />
                </SrFlex>
              )}
            </SrApplicationAssemblyLineContainer>
            <SrBox position='absolute' bottom='20px' width='500px'>
              <SrButton
                theme={SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE}
                onClick={onClose}
              >
                Complete
              </SrButton>
            </SrBox>
          </SrBox>
        );
      }}
    />
  );

  return (
    <SrModalSplitView
      onClose={onClose}
      leftChild={leftChild}
      rightChild={rightChild}
    />
  );
};

const getStepErrors = (pet) => {
  let stepErrors = [];

  stepErrors = lodashCompact([
    pet.letterheadIncluded === false && {
      title: 'Letterhead:',
      message: (
        `Ask the applicant to provide an ESA/Service animal document that
              includes a doctor's letterhead.`
      ),
    },
    pet.letterheadIncludesName === false && {
      title: 'ESA Letter:',
      message: (
        'The owner’s name must be listed on the ESA letter. Please contact the renters.'
      ),
    },
    pet.matchBreed === false && {
      title: 'Breed:',
      message: (
        // eslint-disable-next-line max-len
        'Ask the Applicant to provide a new photo or update their application with the correct breed for this pet.'
      ),
    },
    pet.matchColor === false && {
      title: 'Fur Color:',
      message: (
        // eslint-disable-next-line max-len
        'Ask the Applicant to provide a new photo or update their application with the correct fur color for this pet.'
      ),
    },
    pet.matchWeight === false && {
      title: 'Pet Weight:',
      message: (
        // eslint-disable-next-line max-len
        'Ask the Applicant to provide a new photo or update their application with the correct weight for this pet.'
      ),
    },
    pet.matchType === false && {
      title: 'Pet Type:',
      message: (
        'Ask the Applicant to provide a new photo or update their application with the correct pet type.'
      ),
    },
    pet.isCatOrDog === true && {
      title: 'Pet Type:',
      message: (
        'Ask the Applicant to provide a new photo or update their application with the correct pet type.'
      ),
    },
    pet.isCatOrDog === false && {
      title: 'Pet Type:',
      message: (
      // eslint-disable-next-line max-len
        'Ask your manager for assistance. This pet needs to be sent to the landlord for approval before moving forward with this application.'
      ),
    },
  ]);

  return stepErrors;
};

const isPetScreeningCompleted = ({ pets, partner }) => {
  const petsToVerify = pets.filter((pet) => pet.type === 'dog' || pet.type === 'cat');

  if (petsToVerify.length > 0 && partner?.petScreeningUrl) {
    return petsToVerify.every((pet) => pet.petScreeningProfileId);
  }
  return true;
};

const SrApplicationAssemblyLineStepPets = ({
  assemblyLine,
  householdApplication,
  reloadAssemblyLineData,
  updateAssemblyLineApplication,
}) => {
  const dispatch = useDispatch();
  const { appUrl } = useBranch({ appUrl: ['env', 'APP_URL'] });

  const [viewPetIndex, setViewPetIndex] = useState(null);
  const [manualVerificationInProgress, setManualVerificationInProgress] = useState(false);

  const { pets, nonEsaPets, esaPets } = getPets({ assemblyLine, householdApplication });
  const assemblyLineApplication = householdApplication.assemblyLineApplication || {};
  const partner = assemblyLine.partner || {};
  const petScreeningCompleted = isPetScreeningCompleted({ pets, partner });

  const manuallyVerifyPets = () => {
    setManualVerificationInProgress(true);
    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_APPLICATION_MANUALLY_VERIFY_PETS,
      payload: {
        householdId: householdApplication.householdId,
      },
    }).then(() => {
      setManualVerificationInProgress(false);
      reloadAssemblyLineData();
    });
  };

  return (
    <div>
      <SrApplicationAssemblyLineContainerBoxComparision
        status={nonEsaPets.length > (assemblyLine.listing.maximumPetsAllowed || 0)
          ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED
          : SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
        }
        comparisons={[
          {
            title: 'Number of Pets',
            value: `${pets.length} Pets${esaPets.length ? ` Total (${esaPets.length} ESA Animal)` : ''}`,
          },
          {
            title: 'Number of Pets Allowed',
            value: assemblyLine.listing.maximumPetsAllowed || 0,
          },
        ]}
      />
      {pets.map((pet, petIndex) => {
        const stepErrors = pet.status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS ? getStepErrors(
          assemblyLineApplication.pets[withoutDots(pet.name)]
        ) : [];
        return (
          <SrBox key={pet.name} marginTop='24px'>
            <SrApplicationAssemblyLineContainer
              title={`Pet ${petIndex + 1}`}
              subtitle={pet.name}
              status={pet.status}
              borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
              isExpanded
            >
              <SrButton
                theme={SR_BUTTON_THEME_ASSEMBLY_LINE_BLUE}
                onClick={() => setViewPetIndex(petIndex)}
              >
                View photo
              </SrButton>
              {stepErrors.length > 0 && (
                <SrBox marginTop='24px'>
                  <SrText font='greycliff' size='18' color='orange300' weight='700'>
                    Information Required
                  </SrText>
                  <SrBox height='16px' />
                  <SrErrorMessage theme={SR_ERROR_MESSAGE_THEME_ORANGE}>
                    {stepErrors.map((error) => (
                      <>
                        {error.title}
                        <br />
                        {error.message}
                        <SrBox height='20px' />
                      </>
                    )
                    )}
                  </SrErrorMessage>
                </SrBox>
              )}
            </SrApplicationAssemblyLineContainer>
          </SrBox>
        );
      })}
      {
        !petScreeningCompleted && (
          <SrBox marginTop='24px'>
            <SrApplicationAssemblyLineContainer
              title='Resolve Pending Pet Screening with Manual Verification'
              status={SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED}
              borderType={SR_COLLAPSIBLE_CONTAINER_BORDER_TYPE_ROUNDED}
              isExpanded
              hideIcon
            >
              <SrBox marginBottom='20px'>
                <SrText>
                  Manually verify pet information and mark as verified when household summary
                  card displays 'pending' despite completion of PetScreening process.
                </SrText>
              </SrBox>
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_TRANSPARENT_BLUE_BORDER}
                onClick={manuallyVerifyPets}
                disabled={manualVerificationInProgress}
              >
                {
                  manualVerificationInProgress
                    ? 'Verifiying...'
                    : 'Mark as verified'
                }
              </SrButton>
            </SrApplicationAssemblyLineContainer>
          </SrBox>
        )
      }
      {viewPetIndex !== null && (
        <SrApplicationAssemblyLineStepPetModal
          pet={pets[viewPetIndex]}
          assemblyLine={assemblyLine}
          petIndex={viewPetIndex}
          householdApplication={householdApplication}
          appUrl={appUrl}
          onClose={() => setViewPetIndex(null)}
          updateAssemblyLineApplication={updateAssemblyLineApplication}
        />
      )}
    </div>
  );
};

SrApplicationAssemblyLineStepPets.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
  reloadAssemblyLineData: PropTypes.func.isRequired,
  updateAssemblyLineApplication: PropTypes.func.isRequired,
};

SrApplicationAssemblyLineStepPets.stepTitle = 'Pet Info';
SrApplicationAssemblyLineStepPets.displayName = 'SrApplicationAssemblyLineStepPets';

SrApplicationAssemblyLineStepPets.getStatus = ({ assemblyLine, householdApplication }) => {
  const { pets, nonEsaPets } = getPets({ assemblyLine, householdApplication });

  if (nonEsaPets.length > (assemblyLine.listing.maximumPetsAllowed || 0)) {
    return SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
  }

  const status = getLatestStatus(pets.map(({ status }) => status));
  return status;
};

export default SrApplicationAssemblyLineStepPets;
